import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { ChangeLogEntry } from '../../types';

type Props = {
  rows: ChangeLogEntry[];
  loading?: boolean;
  emptyMessage?: string;
};

function typeBadgeClass(type: string) {
  const normalized = type.trim().toLowerCase();
  if (normalized.includes('invers')) return 'bg-green-100 text-green-700';
  if (normalized.includes('doc')) return 'bg-purple-100 text-purple-700';
  if (normalized.includes('manten') || normalized.includes('pago')) return 'bg-amber-100 text-amber-800';
  return 'bg-blue-100 text-blue-700';
}

function formatHistoryDate(dateStr: string) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, "d MMM yyyy, h:mm aaaa", { locale: es }).replace('.', '');
}

export const HistoryTable: React.FC<Props> = ({
  rows,
  loading = false,
  emptyMessage = 'No hay cambios registrados.',
}) => {
  return (
    <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden bg-white">
      {loading ? (
        <div className="py-12 text-center text-gray-500">Cargando historial...</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-gray-500">{emptyMessage}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['FECHA', 'TIPO', 'DESCRIPCIÓN', 'CAMBIO', 'MODIFICADO POR'].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-xs md:text-sm font-semibold tracking-wide text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-5 py-4 text-sm text-gray-700">{formatHistoryDate(row.createdAt)}</td>
                  <td className="px-5 py-4 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${typeBadgeClass(
                        row.type,
                      )}`}
                    >
                      {row.type}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-800">{row.description}</td>
                  <td className="px-5 py-4 text-sm">
                    {row.oldValue ? (
                      <>
                        <span className="text-red-500 line-through">{row.oldValue}</span>
                        <span className="mx-2 text-gray-400">→</span>
                        <span className="font-semibold text-green-600">{row.newValue || '—'}</span>
                      </>
                    ) : (
                      <span className="font-semibold text-green-600">{row.newValue || '—'}</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">{row.modifiedBy || 'Sistema'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

